import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, TablePagination, Snackbar, Alert, Typography
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { Select, MenuItem, InputLabel, FormControl } from "@mui/material";
import { axiosInstances } from "./apiConfig";

export default function PaymentUI() {

  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [file, setFile] = useState(null);

  // Fetch payments with pagination
  const loadPayments = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances.paymentApi.get(`/?page=${pageNum}&size=${size}`);
      const result = res.data;
      const payload = result.data || {};
      setPayments(payload.data || []);
      setTotal(payload.total || 0);
    } catch (err) {
      console.error("Error fetching payments", err);
    }
  };


  useEffect(() => { loadPayments(); }, [page, rowsPerPage]);

  const handleOpen = (payment = {}) => {
    const transformedPayment = {
      ...payment,
      paymentDate: payment.paymentDate
        ? convertToInputDateFormat(payment.paymentDate)
        : new Date().toISOString().split("T")[0],
    };
    setForm(transformedPayment);
    setEditing(!!payment.id);
    setOpen(true);
  };

  const handleClose = () => { setForm({}); setOpen(false); setFile(null);};

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  function convertToInputDateFormat(dateStr) {
    if (!dateStr || !dateStr.includes("-")) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  function formatDate(inputDate) {
    if (!inputDate) return "";
    const [year, month, day] = inputDate.split("-");
    return `${day}-${month}-${year}`;
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      if (file) {
        // ✅ Check file type for payments
        const allowedTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/vnd.ms-excel", // .xls
          "text/csv", // .csv
        ];

        if (!allowedTypes.includes(file.type)) {
          setSnackbar({ open: true, message: "Only Excel or CSV files are allowed!", severity: "error" });
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        // ✅ Upload payment file
        await axiosInstances.paymentApi.post(`/uploadPayments`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSnackbar({ open: true, message: "Payment file uploaded successfully!", severity: "success" });

      } else {
        // ✅ Manual payment submission (if applicable)
        const paymentPayload = {
          ...form,
          paymentDate: formatDate(form.paymentDate),
          
        };

        if (editing) {
          await axiosInstances.paymentApi.post(`/update`, paymentPayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          setSnackbar({ open: true, message: "Payment updated successfully!", severity: "success" });
        } else {
          await axiosInstances.paymentApi.post(`/save`, paymentPayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          setSnackbar({ open: true, message: "Payment created successfully!", severity: "success" });
        }
      }

      loadPayments();   // 🔁 Refresh payments list
      handleClose();    // ❌ Close modal/dialog
      setFile(null);    // 🧹 Clear file input

    } catch (err) {
      if (err.response && err.response.status === 409) {
        setSnackbar({ open: true, message: "Duplicate payment entry", severity: "error" });
      } else if (err.response && err.response.status === 401) {
        setSnackbar({ open: true, message: "Unauthorized - please log in as admin", severity: "error" });
      } else {
        setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
      }
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDeleteClick = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));

    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.paymentApi.post(`/delete`, { id });
        loadPayments();
      } catch (err) {
        console.error("Delete failed", err);
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndoClick = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Payment Management</Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={() => handleOpen()}
      >
        Add Payment
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Payment Status</TableCell>
              {/* <TableCell>Status</TableCell> */}
              <TableCell>Reference No.</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Payer Name</TableCell>
              <TableCell>Payer Email</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell>Payment Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((pay, index) => (
              <TableRow key={pay.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{pay.id}</TableCell>
                <TableCell>{pay.amount}</TableCell>
                <TableCell>{pay.paymentMethod}</TableCell>
                <TableCell>{pay.p_status}</TableCell>
                {/* <TableCell>{pay.status}</TableCell> */}
                <TableCell>{pay.referenceNumber}</TableCell>
                <TableCell>{pay.currency}</TableCell>
                <TableCell>{pay.payerName}</TableCell>
                <TableCell>{pay.payerEmail}</TableCell>
                <TableCell>{pay.remarks}</TableCell>
                <TableCell>{pay.paymentDate}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(pay)}>
                    <Edit />
                  </IconButton>
                  {pendingDeletes[pay.id] ? (
                    <IconButton color="warning" onClick={() => handleUndoClick(pay.id)}>
                      <Undo />
                    </IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDeleteClick(pay.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) => {
            const totalPages = Math.ceil(total / rowsPerPage);
            return `Page ${page + 1} of ${totalPages}`;
          }}
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? "Edit Payment" : "Add Payment"}</DialogTitle>
        <DialogContent>
          {editing && (
            <TextField
              fullWidth margin="dense" label="Payment ID" name="id"
              value={form.id || ""} disabled
            />
          )}
          <TextField
            fullWidth margin="dense" label="Amount" name="amount" type="number"
            value={form.amount || ""} onChange={handleChange}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Payment Method</InputLabel>
            <Select
              name="paymentMethod"
              value={form.paymentMethod || ""}
              onChange={handleChange}
            >
              <MenuItem value="CARD">Card</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Payment Status</InputLabel>
            <Select
              name="p_status"
              value={form.p_status || ""}
              onChange={handleChange}
            >
              <MenuItem value="SUCCESS">SUCCESS</MenuItem>
              <MenuItem value="FAILURE">FAILURE</MenuItem>
              <MenuItem value="PENDING">PENDING</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth margin="dense" label="Reference Number" name="referenceNumber"
            value={form.referenceNumber || ""} onChange={handleChange}
          />
          <TextField
            fullWidth margin="dense" label="Currency" name="currency"
            value={form.currency || ""} onChange={handleChange}
          />
          <TextField
            fullWidth margin="dense" label="Payer Name" name="payerName"
            value={form.payerName || ""} onChange={handleChange}
          />
          <TextField
            fullWidth margin="dense" label="Payer Email" name="payerEmail"
            value={form.payerEmail || ""} onChange={handleChange}
          />
          <TextField
            fullWidth margin="dense" label="Remarks" name="remarks"
            value={form.remarks || ""} onChange={handleChange}
          />
          <TextField fullWidth margin="dense" label="Payment Date" name="paymentDate" type="date" value={form.paymentDate || new Date().toISOString().split("T")[0]} onChange={handleChange} InputLabelProps={{ shrink: true }} />
          {!editing && (
            <>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
